// AdminSidebar — persona-driven, role-gated navigation.
// Replaces the old single static navSections with the per-persona NAV from the
// design handoff (portal/shell.jsx). Active-state + banner use the persona accent.
import React from 'react';
import { NavLink, useLocation } from 'react-router';
import { Settings as SettingsIcon, ShieldCheck as SecurityIcon } from 'lucide-react';
import { Icon } from '@/components/shared/Icon';
import { useCurrentUser } from '@/context/PersonaProvider';
import { PERSONA, NAV, type NavSection, type PersonaKey } from '@/lib/persona';

/**
 * Drop `staffOnly` items (and any group left empty by that) for non-operators.
 *
 * Fails CLOSED: useCurrentUser() returns null both before /users/me resolves
 * and when the sidebar is rendered outside a PersonaProvider, and either way
 * `isStaff` is false — an unknown user is never treated as an admin. Once the
 * profile lands the item appears.
 *
 * This decides what is DRAWN and nothing more. The route stays mounted, the
 * URL stays reachable, and the licence backend re-authorises every read behind
 * the page. A sidebar is not an access-control decision.
 */
function visibleSections(sections: NavSection[], isStaff: boolean): NavSection[] {
  if (isStaff) return sections;
  return sections
    .map((s) => ({ ...s, items: s.items.filter((i) => !i.staffOnly) }))
    .filter((s) => s.items.length > 0);
}

function activeItemPath(pathname: string, paths: string[], home: string): string | null {
  let best: string | null = null;
  for (const p of paths) {
    const match = p === home ? pathname === home : pathname === p || pathname.startsWith(p + '/');
    if (match && (best === null || p.length > best.length)) best = p;
  }
  return best;
}

export function AdminSidebar({ persona }: { persona: PersonaKey }) {
  const location = useLocation();
  const user = useCurrentUser();
  const p = PERSONA[persona];
  const sections = visibleSections(NAV[persona], !!user?.isAdmin);
  const allPaths = sections.flatMap((s) => s.items.map((i) => i.path));
  const active = activeItemPath(location.pathname, allPaths, p.home);
  const settingsActive = location.pathname === '/settings';
  const securityActive = location.pathname === '/security';

  return (
    <aside
      style={{
        width: 'var(--sidebar-w)',
        background: 'var(--surface)',
        borderRight: '1px solid var(--border)',
        padding: '10px 8px',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        overflowY: 'auto',
        minHeight: 'calc(100vh - var(--header-h))',
      }}
    >
      {/* Persona banner */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 10px 12px', margin: '2px 4px 6px', borderBottom: '1px solid var(--border)' }}>
        <span style={{ width: 30, height: 30, borderRadius: 8, background: p.tint, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name={p.icon} size={16} color={p.accent} />
        </span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.1 }}>{p.label}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.2, marginTop: 1 }}>{p.sub}</div>
        </div>
      </div>

      <div style={{ flex: 1 }}>
        {sections.map((section) => (
          <div key={section.group}>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)', padding: '12px 12px 4px' }}>
              {section.group}
            </div>
            {section.items.map((item) => {
              const on = active === item.path;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === p.home}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 7, fontSize: 13,
                    color: on ? p.accent : 'var(--text-secondary)', background: on ? p.tint : 'transparent',
                    fontWeight: on ? 600 : 400, cursor: 'pointer', textDecoration: 'none', transition: 'all .12s', position: 'relative',
                  }}
                >
                  {on && <span style={{ position: 'absolute', left: 0, top: 6, bottom: 6, width: 3, borderRadius: 2, background: p.accent }} />}
                  <Icon name={item.icon} size={15} />
                  {item.label}
                </NavLink>
              );
            })}
          </div>
        ))}
      </div>

      {/* Shared footer nav */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 6, marginTop: 6 }}>
        <NavLink
          to="/settings"
          style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 7, fontSize: 13,
            color: settingsActive ? p.accent : 'var(--text-secondary)', background: settingsActive ? p.tint : 'transparent',
            fontWeight: settingsActive ? 600 : 400, cursor: 'pointer', textDecoration: 'none',
          }}
        >
          <SettingsIcon size={15} /> Settings
        </NavLink>
        <NavLink
          to="/security"
          style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 7, fontSize: 13,
            color: securityActive ? p.accent : 'var(--text-secondary)', background: securityActive ? p.tint : 'transparent',
            fontWeight: securityActive ? 600 : 400, cursor: 'pointer', textDecoration: 'none',
          }}
        >
          <SecurityIcon size={15} /> Security
        </NavLink>
      </div>
    </aside>
  );
}
